import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Tooltip,
  Avatar,
  Divider,
  IconButton,
  Stack,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Assessment,
  People,
  Event,
  Schedule,
  CheckCircle,
  Cancel,
  Refresh,
  CalendarToday,
  TrendingUp,
  PictureAsPdf,
  Description,
  GetApp,
  Analytics,
  Search,
  FilterList,
  Download,
  Visibility,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { 
  PieChart, Pie, Cell, BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer, LineChart, Line, 
  AreaChart, Area, ComposedChart, Scatter
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AllEmployeesLeaveReports = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [leaveData, setLeaveData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('yearly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({
    totalEmployees: 0,
    totalLeaves: 0,
    approvedLeaves: 0,
    rejectedLeaves: 0,
    pendingLeaves: 0,
    averageLeaveDays: 0,
    leaveUtilization: 0
  });
  const [filteredData, setFilteredData] = useState([]);
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    fetchAllLeaveData();
  }, [selectedPeriod, selectedYear]);

  useEffect(() => {
    calculateSummary(leaveData);
    filterData();
  }, [leaveData, searchQuery]);

  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const employee_id = payload.employee_id || '';
      
      const company_id = payload.company_id || payload.company_registration_no;
      if (company_id) {
        try {
          const cmpRes = await fetch(`${API_URL}/api/cmp/${company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (cmpRes.ok) {
            const cmpData = await cmpRes.json();
            if (cmpData.data) {
              setCompanyConfig({
                dateFormat: cmpData.data.dateFormat || 'DD/MM/YYYY',
                timezone: cmpData.data.timezone || 'Asia/Kolkata'
              });
            }
          }
        } catch (e) {}
      }

      const response = await fetch(`${API_URL}/api/leave-report/years?employee_id=${employee_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.years && data.years.length > 0) {
          setAvailableYears(data.years);
          // Keep current year selected by default
          if (!data.years.includes(selectedYear)) {
            setSelectedYear(data.years[0]);
          }
        }
      }
    } catch (e) {
      //console.error('Error fetching available years:', e);
    }
  };

  const fetchAllLeaveData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) { alert('No authentication token found. Please log in.'); return; }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const employee_id = payload.employee_id || '';

      const yearParam = selectedPeriod === 'yearly' ? `&year=${selectedYear}` : '';
      const response = await fetch(
        `${API_URL}/api/leave-report/summary?period=${selectedPeriod}${yearParam}&employee_id=${employee_id}`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (!response.ok) throw new Error('Failed to fetch leave data');

      const result = await response.json();
      const data = result.leaveData || [];
      setLeaveData(data);
      setFilteredData(data);
      if (result.summary) setSummary(result.summary);
    } catch (error) {
      //console.error('Error fetching leave data:', error);
      setError('Failed to fetch leave data. Please try again.');
      setLeaveData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    if (!searchQuery.trim()) {
      setFilteredData(leaveData);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = leaveData.filter(leave => 
      leave.employeeName.toLowerCase().includes(query) ||
      leave.department.toLowerCase().includes(query) ||
      leave.leave_type.toLowerCase().includes(query) ||
      leave.status.toLowerCase().includes(query) ||
      leave.reason.toLowerCase().includes(query)
    );
    
    setFilteredData(filtered);
  };

  const calculateSummary = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      setSummary({
        totalEmployees: 0,
        totalLeaves: 0,
        approvedLeaves: 0,
        rejectedLeaves: 0,
        pendingLeaves: 0,
        averageLeaveDays: 0,
        leaveUtilization: 0
      });
      return;
    }

    const uniqueEmployees = new Set(data.map(leave => leave.employee_id));
    const approvedLeaves = data.filter(leave => leave.status?.toLowerCase() === 'approved');
    const rejectedLeaves = data.filter(leave => leave.status?.toLowerCase() === 'rejected');
    const pendingLeaves = data.filter(leave => leave.status?.toLowerCase() === 'pending');
    
    const totalDays = data.reduce((sum, leave) => sum + (leave.number_of_days || 0), 0);
    const averageLeaveDays = data.length > 0 ? (totalDays / data.length).toFixed(1) : 0;
    
    // Calculate leave utilization percentage (assuming 20 working days per month per employee)
    const totalWorkingDays = uniqueEmployees.size * 20;
    const leaveUtilization = totalWorkingDays > 0 ? ((totalDays / totalWorkingDays) * 100).toFixed(1) : 0;
    
    setSummary({
      totalEmployees: uniqueEmployees.size,
      totalLeaves: data.length,
      approvedLeaves: approvedLeaves.length,
      rejectedLeaves: rejectedLeaves.length,
      pendingLeaves: pendingLeaves.length,
      averageLeaveDays: parseFloat(averageLeaveDays),
      leaveUtilization: parseFloat(leaveUtilization)
    });
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  const handleRefresh = () => {
    fetchAllLeaveData();
  };

  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { alert('No authentication token found. Please log in.'); return; }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const employee_id = payload.employee_id || '';

      const ext = format === 'docx' ? 'doc' : 'pdf';
      const yearParam = selectedPeriod === 'yearly' ? `&year=${selectedYear}` : '';
      const url = `${API_URL}/api/leave-report/export/${ext}?period=${selectedPeriod}${yearParam}&employee_id=${employee_id}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to export data');

      const blob = await response.blob();
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = window.URL.createObjectURL(blob);
      link.download = `Leave-Report-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    } catch (error) {
      //console.error('Export error:', error);
      setError('Failed to export data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const getLeaveTypeDistribution = () => {
    if (!Array.isArray(leaveData) || leaveData.length === 0) return [];
    
    const distribution = {};
    leaveData.forEach(leave => {
      const type = leave.leave_type || 'unknown';
      distribution[type] = (distribution[type] || 0) + 1;
    });
    
    return Object.entries(distribution).map(([type, count]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: count,
      percentage: ((count / leaveData.length) * 100).toFixed(1)
    }));
  };

  const getMonthlyDistribution = () => {
    if (!Array.isArray(leaveData) || leaveData.length === 0) return [];
    
    const distribution = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // Use selectedYear for yearly period, otherwise current year
    const targetYear = selectedPeriod === 'yearly' ? selectedYear : new Date().getFullYear();
    
    // Initialize all months with 0
    months.forEach(month => {
      distribution[month] = 0;
    });
    
    // Fill with actual data — only include leaves from the current year
    leaveData.forEach(leave => {
      if (leave.start_date) {
        const leaveDate = new Date(leave.start_date);
        if (leaveDate.getFullYear() !== targetYear) return; // skip other years
        const month = leaveDate.toLocaleString('default', { month: 'short' });
        distribution[month] = (distribution[month] || 0) + 1;
      }
    });
    
    return months.map(month => ({ 
      month, 
      leaves: distribution[month],
      approved: leaveData.filter(l => {
        if (!l.start_date) return false;
        const d = new Date(l.start_date);
        return d.getFullYear() === targetYear &&
          d.toLocaleString('default', { month: 'short' }) === month &&
          l.status?.toLowerCase() === 'approved';
      }).length,
      pending: leaveData.filter(l => {
        if (!l.start_date) return false;
        const d = new Date(l.start_date);
        return d.getFullYear() === targetYear &&
          d.toLocaleString('default', { month: 'short' }) === month &&
          l.status?.toLowerCase() === 'pending';
      }).length
    }));
  };


  const getStatusDistribution = () => {
    if (!Array.isArray(leaveData) || leaveData.length === 0) return [];
    
    const statusCounts = {
      approved: 0,
      pending: 0,
      rejected: 0
    };
    
    leaveData.forEach(leave => {
      const status = leave.status ? leave.status.toLowerCase() : 'pending';
      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      }
    });
    
    return [
      { name: 'Approved', value: statusCounts.approved, color: '#4caf50' },
      { name: 'Pending', value: statusCounts.pending, color: '#ff9800' },
      { name: 'Rejected', value: statusCounts.rejected, color: '#f44336' }
    ].filter(item => item.value > 0);
  };

  const getEmployeeLeaveStats = () => {
    if (!Array.isArray(leaveData) || leaveData.length === 0) return [];
    
    const stats = {};
    leaveData.forEach(leave => {
      const empId = leave.employee_id;
      if (!stats[empId]) {
        stats[empId] = {
          employee_id: empId,
          employeeName: leave.employeeName,
          department: leave.department || 'N/A',
          totalLeaves: 0,
          approved: 0,
          rejected: 0,
          pending: 0,
          annual: 0,
          sick: 0,
          casual: 0,
          other: 0,
          totalDays: 0
        };
      }
      
      stats[empId].totalLeaves++;
      stats[empId].totalDays += parseInt(leave.number_of_days) || 0;
      
      const status = leave.status ? leave.status.toLowerCase() : 'pending';
      if (status === 'approved') stats[empId].approved++;
      else if (status === 'rejected') stats[empId].rejected++;
      else stats[empId].pending++;
      
      const leaveType = leave.leave_type ? leave.leave_type.toLowerCase() : 'unknown';
      if (leaveType === 'annual') stats[empId].annual++;
      else if (leaveType === 'sick') stats[empId].sick++;
      else if (leaveType === 'casual') stats[empId].casual++;
      else stats[empId].other++;
    });
    
    return Object.values(stats).sort((a, b) => b.totalDays - a.totalDays);
  };

  const getDepartmentDistribution = () => {
    if (!Array.isArray(leaveData) || leaveData.length === 0) return [];
    
    const distribution = {};
    leaveData.forEach(leave => {
      const dept = leave.department || 'Not Specified';
      distribution[dept] = (distribution[dept] || 0) + 1;
    });
    
    return Object.entries(distribution).map(([dept, count]) => ({
      name: dept,
      value: count
    }));
  };

  const getTopEmployees = () => {
    const stats = getEmployeeLeaveStats();
    return stats.slice(0, 5);
  };

  const getRecentLeaves = () => {
    return [...filteredData]
      .sort((a, b) => new Date(b.applied_date || b.created_at) - new Date(a.applied_date || a.created_at))
      .slice(0, 5);
  };

  const renderSummaryCards = () => {
    const cards = [
    
      {
        title: 'Total Leaves',
        value: summary.totalLeaves,
        icon: <Event sx={{ fontSize: 28 }} />,
        color: '#7b1fa2',
      
      },
      {
        title: 'Approved',
        value: summary.approvedLeaves,
        icon: <CheckCircle sx={{ fontSize: 28 }} />,
        color: '#2e7d32',
      
      },
      {
        title: 'Pending',
        value: summary.pendingLeaves,
        icon: <Schedule sx={{ fontSize: 28 }} />,
        color: '#f57c00',
      
      },
      {
        title: 'Rejected',
        value: summary.rejectedLeaves,
        icon: <Cancel sx={{ fontSize: 28 }} />,
        color: '#d32f2f',
       
      },
    
    ];

    return (
      <Grid container spacing={3}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} lg={3} key={index}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              border: '1px solid #e3f2fd', // Light blue border for professional touch
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                borderColor: '#bbdefb' // Slightly darker light blue on hover
              }
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {card.value}
                      {card.suffix || ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {card.title}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    bgcolor: `${card.color}15`,
                    p: 1.5,
                    borderRadius: 2
                  }}>
                    {card.icon}
                  </Box>
                </Box>
                {card.trend && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    {card.trend.startsWith('+') ? (
                      <ArrowUpward sx={{ fontSize: 16, color: '#2e7d32', mr: 0.5 }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 16, color: '#d32f2f', mr: 0.5 }} />
                    )}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: card.trend.startsWith('+') ? '#2e7d32' : '#d32f2f',
                        fontWeight: 500
                      }}
                    >
                      {card.trend} from last period
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderChartsSection = () => {
    const monthlyData = getMonthlyDistribution();
    const statusData = getStatusDistribution();
    const leaveTypes = getLeaveTypeDistribution();
    const departmentData = getDepartmentDistribution();

    return (
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Monthly Trend Chart */}
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%',width:'600px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Monthly Leave Trends
              </Typography>
              <Chip 
                label={selectedPeriod.toUpperCase()} 
                color="primary" 
                size="small"
                sx={{ borderRadius: 2 }}
              />
            </Box>
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="leaves" name="Total Leaves" fill="#8884d8" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="approved" name="Approved" stroke="#4caf50" strokeWidth={2} />
                  <Line type="monotone" dataKey="pending" name="Pending" stroke="#ff9800" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Status Distribution */}
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%',width:'500px' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
              Status Distribution
            </Typography>
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => [value, 'Leaves']} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              {statusData.map((status, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    bgcolor: status.color,
                    borderRadius: '50%',
                    mr: 1
                  }} />
                  <Typography variant="caption">{status.name}: {status.value}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

    

       
      </Grid>
    );
  };



  const renderRecentLeaves = () => {
    const recentLeaves = getRecentLeaves();
    
    return (
      <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
          Recent Leave Applications
        </Typography>
        {recentLeaves.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell>Employee</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Applied Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentLeaves.map((leave, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 30, height: 30 }}>
                          {leave.employeeName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {leave.employeeName}
                          </Typography>
                         
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={leave.leave_type?.toUpperCase() || 'N/A'} 
                        size="small"
                        sx={{ 
                          borderRadius: 1,
                          bgcolor: leave.leave_type === 'annual' ? '#e3f2fd' :
                                   leave.leave_type === 'sick' ? '#ffebee' :
                                   leave.leave_type === 'casual' ? '#e8f5e8' : '#f5f5f5'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {dayjs(leave.start_date).tz(companyConfig.timezone).format(companyConfig.dateFormat)} - {dayjs(leave.end_date).tz(companyConfig.timezone).format(companyConfig.dateFormat)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({leave.number_of_days || 0} days)
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={leave.status?.toUpperCase() || 'PENDING'} 
                        size="small"
                        color={
                          leave.status?.toLowerCase() === 'approved' ? 'success' :
                          leave.status?.toLowerCase() === 'rejected' ? 'error' : 'warning'
                        }
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      {dayjs(leave.applied_date || leave.created_at).tz(companyConfig.timezone).format(companyConfig.dateFormat)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Event sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No recent leave applications
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  const renderHeader = () => (
    <Box sx={{ 
      mb: 4,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 2
    }}>
      <Box>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Leave Report
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive overview of employee leave patterns and trends
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search employees, departments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ 
            width: 250,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            label="Period"
            onChange={handlePeriodChange}
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>

        {/* Year dropdown — only visible when Yearly is selected */}
        {selectedPeriod === 'yearly' && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {availableYears.map(yr => (
                <MenuItem key={yr} value={yr}>
                  {yr} {yr === new Date().getFullYear() ? '(Current)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        <Button
          variant="contained"
          startIcon={<PictureAsPdf />}
          onClick={() => handleExport('pdf')}
          disabled={exportLoading || loading}
          sx={{ borderRadius: 2 }}
        >
          Export PDF
        </Button>
        <Button
          variant="outlined"
          startIcon={<Description />}
          onClick={() => handleExport('docx')}
          disabled={exportLoading || loading}
          sx={{ borderRadius: 2 }}
        >
          Export DOCX
        </Button>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading leave analytics dashboard...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {renderHeader()}
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      
      {/* Summary Cards */}
      <Box sx={{ mb: 4 }}>
        {renderSummaryCards()}
      </Box>
      
      <Grid item xs={12} lg={6}>
      {renderChartsSection()}
   
          {renderRecentLeaves()}
        </Grid>
    
      
      {/* Export Loading Overlay */}
      {exportLoading && (
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
            <CircularProgress size={50} sx={{ mb: 2 }} />
            <Typography variant="h6" fontWeight="bold">
              Generating Report...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Preparing your comprehensive leave report
            </Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default AllEmployeesLeaveReports;